// Gallery.jsx
// ─ Lovable, cheerful, Japanese-family-friendly redesign
// ─ Matches BuggySection + FoodSection design system
// ─ Rainbow section label, chunky shadow cards, warm palette
// ─ Pure Tailwind, bilingual ja/en, lightbox with prev/next
// ─ Emojis removed from cards, hints and lightbox

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiInstagram, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

/* ─────────────────────────────────────────────────────────
   DATA — no emoji field
───────────────────────────────────────────────────────── */
const ITEMS = [
  { id:1, image:'/images/buggyteam2.jpeg', titleJa:'バギー体験',     titleEn:'Buggy Ride',     cat:'人気No.1',   size:'large',  bg:'from-[#1a3d20] to-[#2d6b38]' },
  { id:2, image:'/images/papri.jpg',   titleJa:'森のオフロード', titleEn:'Forest Trail',   cat:'Nature',     size:'tall',   bg:'from-[#1a2a3d] to-[#2d4a6b]' },
  { id:3, image:'/images/buggy.JPG',       titleJa:'家族で楽しむ',   titleEn:'Family Fun',     cat:'Family',     size:'normal', bg:'from-[#3d1a1a] to-[#6b2d2d]' },
  { id:4, image:'/images/trampoline.png',  titleJa:'トランポリン',   titleEn:'Trampoline',     cat:'Activity',   size:'wide',   bg:'from-[#3a2010] to-[#6b3a1f]' },
  { id:5, image:'/images/naan.png',        titleJa:'ナン焼き体験',   titleEn:'Naan Baking',    cat:'Food',       size:'normal', bg:'from-[#1a3a20] to-[#2d6b38]' },
  { id:6, image:'/images/chainsaw.png',    titleJa:'チェンソー体験', titleEn:'Chainsaw Art',   cat:'Adventure',  size:'tall',   bg:'from-[#2d1a3d] to-[#5a3a6b]' },
  { id:7, image:'/images/vegetable.png',   titleJa:'野菜収穫',       titleEn:'Veggie Harvest', cat:'Farm',       size:'normal', bg:'from-[#3a3010] to-[#6b5a1f]' },
  { id:8, image:'/images/paprika.jpg',     titleJa:'ネパール料理',   titleEn:'Nepali Food',    cat:'Restaurant', size:'large',  bg:'from-[#1a2a0a] to-[#3d5a14]' },
];

/* ─────────────────────────────────────────────────────────
   GRID SPAN CLASSES
───────────────────────────────────────────────────────── */
const sizeClass = {
  large:  'col-span-2 row-span-2',
  wide:   'col-span-2',
  tall:   'row-span-2',
  normal: '',
};

/* ─────────────────────────────────────────────────────────
   IMAGE WITH GRADIENT FALLBACK — no emoji
───────────────────────────────────────────────────────── */
function GalleryImg({ src, alt, bg }) {
  return (
    <>
      {/* gradient shown behind image as fallback */}
      <div className={`absolute inset-0 bg-gradient-to-br ${bg}`} />
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.08]"
        onError={e => { e.target.style.display = 'none'; }}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
export default function Gallery() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isJa = lang?.startsWith('ja');

  const [selected, setSelected] = useState(null);

  /* keyboard navigation for lightbox */
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft')  prevImage();
      if (e.key === 'Escape')     setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const nextImage = () => {
    const i = ITEMS.findIndex(x => x.id === selected.id);
    setSelected(ITEMS[(i + 1) % ITEMS.length]);
  };
  const prevImage = () => {
    const i = ITEMS.findIndex(x => x.id === selected.id);
    setSelected(ITEMS[(i - 1 + ITEMS.length) % ITEMS.length]);
  };

  return (
    <section id="gallery" className="relative bg-[#f8f5ef] py-16 overflow-hidden">

      {/* dot pattern */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage:'radial-gradient(circle,rgba(30,80,30,.04) 1px,transparent 1px)', backgroundSize:'24px 24px' }}/>
      <div className="absolute top-0 left-0 text-[180px] opacity-[0.025] select-none pointer-events-none leading-none">🌿</div>
      <div className="absolute bottom-0 right-0 text-[180px] opacity-[0.025] select-none pointer-events-none leading-none">🍃</div>

      <div className="max-w-7xl mx-auto px-5 relative z-10">

        {/* ── SECTION LABEL ── */}
        <div className="flex items-center justify-center gap-4 mb-5">
          <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-r from-transparent to-[#2d6b38]"/>
          <span className="text-[11px] font-extrabold tracking-[.35em] uppercase text-[#2d6b38]">
            Photo Gallery · ギャラリー
          </span>
          <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-l from-transparent to-[#2d6b38]"/>
        </div>

        {/* ══ HEADER ══ */}
        <motion.div
          initial={{ opacity:0, y:40 }} whileInView={{ opacity:1, y:0 }}
          transition={{ duration:.7 }} viewport={{ once:true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 bg-[#1f4d2b] text-white rounded-full px-6 py-3 font-extrabold text-[13px] tracking-wide mb-5 shadow-[0_5px_0_#0d2810]">
            <span className="w-2 h-2 rounded-full bg-[#7dc45a] animate-pulse"/>
            Photo Gallery
          </div>

          <h2 className="font-extrabold text-[#173617] leading-[.92] tracking-tight mb-4"
            style={{ fontSize:'clamp(40px,7vw,68px)' }}>
            {isJa ? (
              <>思い出の<span className="text-[#2d6b38]">ギャラリー</span></>
            ) : (
              <>Photo <span className="text-[#2d6b38]">Gallery</span></>
            )}
          </h2>

          <p className="text-[15px] font-bold text-[#666] max-w-lg mx-auto leading-[1.8]">
            {isJa
              ? '掛川バギーでの体験をご覧ください。家族の笑顔・自然・感動の瞬間。'
              : 'Memories, smiles and adventures at Kakegawa Buggy.'}
            <span className="block text-[12px] text-[#999] mt-1">
              {isJa
                ? 'Memories, smiles and adventures at Kakegawa Buggy'
                : '掛川バギーでの体験・家族の笑顔・感動の瞬間'}
            </span>
          </p>

          <div className="flex items-center justify-center gap-4 mt-5">
            <div className="w-20 h-[3px] rounded-full bg-[#d2b48c]"/>
            <div className="w-20 h-[3px] rounded-full bg-[#d2b48c]"/>
          </div>
        </motion.div>

        {/* ══ MASONRY GRID ══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[200px] gap-3 mb-12">
          {ITEMS.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity:0, y:40 }}
              whileInView={{ opacity:1, y:0 }}
              transition={{ duration:.6, delay: index * 0.05 }}
              viewport={{ once:true }}
              onClick={() => setSelected(item)}
              className={[
                'group relative overflow-hidden rounded-[22px] cursor-pointer',
                'border-[2.5px] border-[#e6d8c3]',
                'shadow-[0_5px_0_#e6d8c3,0_8px_20px_rgba(30,50,20,.07)]',
                'transition-transform duration-250 hover:-translate-y-[6px]',
                sizeClass[item.size] || '',
              ].join(' ')}
            >
              <GalleryImg
                src={item.image}
                alt={isJa ? item.titleJa : item.titleEn}
                bg={item.bg}
              />

              {/* gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent"/>

              {/* category pill — no emoji */}
              <div className="
                absolute top-3 left-3 z-10
                bg-[#173617]/90 backdrop-blur-md
                text-white text-[11px] font-extrabold
                px-3.5 py-1.5 rounded-full
                shadow-[2px_2px_0_#0d2010]
              ">
                {item.cat}
              </div>

              {/* title — no hint text */}
              <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                <h3 className="text-white font-extrabold leading-tight drop-shadow-lg"
                  style={{ fontSize: item.size === 'large' ? 'clamp(18px,2.5vw,24px)' : 'clamp(14px,1.8vw,18px)' }}>
                  {isJa ? item.titleJa : item.titleEn}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ══ INSTAGRAM CTA ══ */}
        <motion.div
          initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }}
          transition={{ duration:.6 }} viewport={{ once:true }}
          className="flex justify-center"
        >
          <motion.a
            href="https://www.instagram.com/kakegawabuggy"
            target="_blank"
            rel="noreferrer"
            whileHover={{ y:-3 }}
            whileTap={{ scale:.97 }}
            className="
              relative overflow-hidden
              inline-flex items-center gap-4
              rounded-full px-10 py-5
              text-white font-extrabold text-[16px]
              shadow-[0_8px_0_rgba(0,0,0,.2),0_12px_32px_rgba(130,50,200,.3)]
              no-underline
            "
            style={{ background:'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)' }}
          >
            <span className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none"/>
            <FiInstagram size={24}/>
            {isJa
              ? 'Instagramで #掛川バギー をチェック！'
              : 'Follow us on Instagram!'}
          </motion.a>
        </motion.div>

      </div>

      {/* ══ LIGHTBOX ══ */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-[9999] bg-black/92 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setSelected(null)}
          >
            {/* close */}
            <button
              onClick={() => setSelected(null)}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
            >
              <FiX size={22}/>
            </button>

            {/* prev */}
            <button
              onClick={e => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
            >
              <FiChevronLeft size={26}/>
            </button>

            {/* next */}
            <button
              onClick={e => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition z-10"
            >
              <FiChevronRight size={26}/>
            </button>

            {/* image */}
            <motion.div
              key={selected.id}
              initial={{ scale:.88, opacity:0 }}
              animate={{ scale:1, opacity:1 }}
              exit={{ scale:.88, opacity:0 }}
              transition={{ type:'spring', stiffness:260, damping:24 }}
              onClick={e => e.stopPropagation()}
              className="max-w-5xl w-full relative"
            >
              {/* gradient fallback — no emoji */}
              <div className={`w-full max-h-[80vh] rounded-[24px] overflow-hidden bg-gradient-to-br ${selected.bg} min-h-[300px] relative`}>
                <img
                  src={selected.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                  onError={e => { e.target.style.display = 'none'; }}
                />
                {/* bottom text overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-7 bg-gradient-to-t from-black/85 to-transparent rounded-b-[24px] z-10">
                  <p className="text-white font-extrabold mb-1.5" style={{ fontSize:'clamp(22px,3vw,34px)' }}>
                    {isJa ? selected.titleJa : selected.titleEn}
                  </p>
                  <p className="text-white/75 text-[13px] font-bold">{selected.cat}</p>
                </div>
              </div>

              {/* counter */}
              <p className="text-center text-white/45 text-[12px] font-bold tracking-widest mt-4">
                {ITEMS.findIndex(x => x.id === selected.id) + 1} / {ITEMS.length}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}